import { useState } from "react";
import * as Y from "yjs";
import {
  applyTableView,
  newId,
  type CellType,
  type CellValue,
  type FilterOperator,
  type TableColumn,
  type TableFilter,
  type TableRow,
  type TableView,
} from "@worktools/shared";
import { useBlockProps, useSharedProp } from "../../sync/hooks";
import { IconPlus, IconTrash } from "../icons";

const OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: "contains", label: "enthält" },
  { value: "notContains", label: "enthält nicht" },
  { value: "eq", label: "ist" },
  { value: "neq", label: "ist nicht" },
  { value: "gt", label: ">" },
  { value: "lt", label: "<" },
  { value: "isEmpty", label: "ist leer" },
  { value: "isNotEmpty", label: "ist gefüllt" },
  { value: "isTrue", label: "ist an" },
  { value: "isFalse", label: "ist aus" },
];

const TYPES: { value: CellType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Zahl" },
  { value: "checkbox", label: "Checkbox" },
  { value: "date", label: "Datum" },
  { value: "select", label: "Auswahl" },
];

export function TableBlock({ blockId }: { blockId: string }) {
  const props = useBlockProps<{ columns: TableColumn[]; rows: TableRow[]; view: TableView }>(blockId);
  const columnsArray = useSharedProp<Y.Array<Y.Map<any>>>(blockId, "columns");
  const rowsArray = useSharedProp<Y.Array<Y.Map<any>>>(blockId, "rows");
  const viewMap = useSharedProp<Y.Map<any>>(blockId, "view");
  const [menuColumn, setMenuColumn] = useState<string | null>(null);

  const columns = props.columns ?? [];
  const rows = props.rows ?? [];
  const view = props.view ?? {};
  const visible = applyTableView(rows, columns, view);

  const setView = (patch: Partial<TableView>) => {
    viewMap?.doc?.transact(() => {
      for (const [key, value] of Object.entries(patch)) viewMap.set(key, value);
    });
  };

  const sortBy = (columnId: string) => {
    const current = view.sort;
    if (current?.columnId !== columnId) return setView({ sort: { columnId, direction: "asc" } });
    if (current.direction === "asc") return setView({ sort: { columnId, direction: "desc" } });
    return setView({ sort: null });
  };

  const addColumn = () => {
    if (!columnsArray) return;
    const column = new Y.Map<any>();
    column.set("id", newId("c"));
    column.set("label", `Spalte ${columns.length + 1}`);
    column.set("type", "text");
    column.set("width", 160);
    columnsArray.push([column]);
  };

  const updateColumn = (index: number, patch: Partial<TableColumn>) => {
    const column = columnsArray?.get(index);
    column?.doc?.transact(() => {
      for (const [key, value] of Object.entries(patch)) column.set(key, value);
    });
  };

  const removeColumn = (index: number) => {
    const columnId = columns[index]?.id;
    columnsArray?.doc?.transact(() => {
      columnsArray.delete(index, 1);
      rowsArray?.forEach((row) => (row.get("cells") as Y.Map<any>)?.delete(columnId));
    });
    setMenuColumn(null);
  };

  const addRow = () => {
    if (!rowsArray) return;
    const row = new Y.Map<any>();
    row.set("id", newId("r"));
    const cells = new Y.Map<any>();
    for (const column of columns) cells.set(column.id, column.type === "checkbox" ? false : "");
    row.set("cells", cells);
    rowsArray.push([row]);
  };

  const setCell = (rowId: string, columnId: string, value: CellValue) => {
    const index = rows.findIndex((row) => row.id === rowId);
    const cells = rowsArray?.get(index)?.get("cells") as Y.Map<any> | undefined;
    cells?.set(columnId, value);
  };

  const removeRow = (rowId: string) => {
    const index = rows.findIndex((row) => row.id === rowId);
    if (index >= 0) rowsArray?.delete(index, 1);
  };

  const filters = view.filters ?? [];
  const updateFilter = (index: number, patch: Partial<TableFilter>) =>
    setView({ filters: filters.map((filter, i) => (i === index ? { ...filter, ...patch } : filter)) });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          type="text"
          value={view.search ?? ""}
          placeholder="Suchen…"
          onChange={(event) => setView({ search: event.target.value })}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button
          className="pill"
          onClick={() =>
            setView({
              filters: [...filters, { columnId: columns[0]?.id ?? "", operator: "contains", value: "" }],
            })
          }
        >
          Filter
        </button>
        <button className="pill" onClick={addColumn}>
          Spalte
        </button>
        <button className="pill pill--solid" onClick={addRow}>
          <IconPlus size={12} /> Zeile
        </button>
      </div>

      {filters.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {filters.map((filter, index) => (
            <div key={index} className="filter-chip">
              <select
                value={filter.columnId}
                onChange={(event) => updateFilter(index, { columnId: event.target.value })}
              >
                {columns.map((column) => (
                  <option key={column.id} value={column.id}>
                    {column.label}
                  </option>
                ))}
              </select>
              <select
                value={filter.operator}
                onChange={(event) => updateFilter(index, { operator: event.target.value as FilterOperator })}
              >
                {OPERATORS.map((operator) => (
                  <option key={operator.value} value={operator.value}>
                    {operator.label}
                  </option>
                ))}
              </select>
              {!["isEmpty", "isNotEmpty", "isTrue", "isFalse"].includes(filter.operator) && (
                <input
                  type="text"
                  value={String(filter.value ?? "")}
                  onChange={(event) => updateFilter(index, { value: event.target.value })}
                  style={{ width: 90 }}
                />
              )}
              <button
                className="row-action"
                onClick={() => setView({ filters: filters.filter((_, i) => i !== index) })}
              >
                <IconTrash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflow: "auto" }} className="scroll">
        <table className="dt">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={column.id} style={{ width: column.width }}>
                  <div className="dt__head">
                    <button className="dt__sort" onClick={() => sortBy(column.id)}>
                      {column.label}
                      {view.sort?.columnId === column.id && (
                        <span>{view.sort.direction === "asc" ? "↑" : "↓"}</span>
                      )}
                    </button>
                    <button
                      className="row-action"
                      onClick={() => setMenuColumn(menuColumn === column.id ? null : column.id)}
                    >
                      ⋯
                    </button>
                  </div>
                  {menuColumn === column.id && (
                    <div className="dt__menu">
                      <input
                        type="text"
                        value={column.label}
                        onChange={(event) => updateColumn(index, { label: event.target.value })}
                      />
                      <select
                        value={column.type}
                        onChange={(event) => updateColumn(index, { type: event.target.value as CellType })}
                      >
                        {TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={column.width}
                        onChange={(event) => updateColumn(index, { width: Number(event.target.value) })}
                      />
                      <button className="pill pill--danger" onClick={() => removeColumn(index)}>
                        Spalte löschen
                      </button>
                    </div>
                  )}
                </th>
              ))}
              <th style={{ width: 32 }} />
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id}>
                {columns.map((column) => (
                  <td key={column.id}>
                    <Cell
                      type={column.type}
                      value={row.cells[column.id] ?? null}
                      options={column.options}
                      onChange={(value) => setCell(row.id, column.id, value)}
                    />
                  </td>
                ))}
                <td>
                  <button className="row-action" onClick={() => removeRow(row.id)}>
                    <IconTrash size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && (
          <div className="label" style={{ padding: "14px 4px" }}>
            {rows.length === 0 ? "Noch keine Zeilen" : "Keine Treffer"}
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({
  type,
  value,
  options,
  onChange,
}: {
  type: CellType;
  value: CellValue;
  options?: string[];
  onChange: (value: CellValue) => void;
}) {
  if (type === "checkbox") {
    return (
      <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
    );
  }
  if (type === "select") {
    return (
      <select value={String(value ?? "")} onChange={(event) => onChange(event.target.value)}>
        <option value="" />
        {(options ?? []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={type === "number" ? "number" : type === "date" ? "date" : "text"}
      value={String(value ?? "")}
      onChange={(event) => onChange(type === "number" ? Number(event.target.value) : event.target.value)}
      className="dt__input"
    />
  );
}
